import { CoreError } from './result';

/**
 * The subset of the Web Crypto API we rely on. It is available as `globalThis.crypto` in
 * browsers, Cloudflare Workers and Node ≥ 19, so this package needs no DOM/Node typings.
 */
interface MinimalCrypto {
  getRandomValues<T extends Uint8Array>(array: T): T;
  readonly subtle: {
    digest(algorithm: 'SHA-256', data: Uint8Array): Promise<ArrayBuffer>;
  };
}

function webCrypto(): MinimalCrypto {
  const c = (globalThis as { crypto?: MinimalCrypto }).crypto;
  if (c === undefined)
    throw new CoreError('NO_CRYPTO', 'Web Crypto API (globalThis.crypto) is not available');
  return c;
}

/** Cryptographically secure random bytes (`crypto.getRandomValues`). */
export function randomBytes(length: number): Uint8Array {
  if (!Number.isSafeInteger(length) || length < 0 || length > 65536) {
    throw new CoreError('INVALID_LENGTH', `Invalid random byte length ${length}`);
  }
  return webCrypto().getRandomValues(new Uint8Array(length));
}

/** SHA-256 digest via `crypto.subtle`. */
export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await webCrypto().subtle.digest('SHA-256', data));
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** RFC 4648 base64url without padding. */
export function bytesToBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = bytes[i + 1] ?? 0;
    const b2 = bytes[i + 2] ?? 0;
    const n = (b0 << 16) | (b1 << 8) | b2;
    const chars = [(n >> 18) & 63, (n >> 12) & 63, (n >> 6) & 63, n & 63];
    const count = Math.min(4, Math.ceil(((bytes.length - i) * 8) / 6));
    for (let k = 0; k < count; k++) out += B64URL.charAt(chars[k] ?? 0);
  }
  return out;
}

/** Lower-case hex encoding. */
export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Random identifier: 128 bits as 22 base64url characters (URL- and filename-safe).
 * Suitable for account, group, expense, member and invite ids.
 */
export function generateId(): string {
  return bytesToBase64Url(randomBytes(16));
}

/** Random secret token (default 256 bits) as base64url, e.g. a device account secret. */
export function generateToken(bytes = 32): string {
  return bytesToBase64Url(randomBytes(bytes));
}
